// TeamViewMobileLayout: Mobile layout for TeamView
// Sticky header with date selector + filters, scrollable timeline content
// Extracted from TeamView.tsx to separate layout concerns
// UPDATED: Added print button (Printer icon) for PDF schedule export
// UPDATED: Added list view (agenda) with ViewSwitch, PeriodNavigator, and UpcomingMissionsAgenda
// UPDATED: PDF export button now available for both calendar and list views

import { AlertCircle, Layers, Printer, RefreshCw, Users } from 'lucide-react';
import { Z_STICKY_HEADER } from '../../lib/zIndex';
import { SiteSelector } from '../manager/SiteSelector';
import { ServiceSelector } from '../manager/ServiceSelector';
import { MultiProfileSelector } from '../manager/MultiProfileSelector';
import { DayTimeline } from '../manager/DayTimeline';
import { ConflictSummaryPanel } from '../manager/ConflictSummaryPanel';
import { TimelineLoadingState } from '../ui/TimelineLoadingState';
import { InlineMonthCalendar } from '../ui/InlineMonthCalendar';
import { DateSelectorButton } from './DateSelectorButton';
import { ViewSwitch } from '../ui/ViewSwitch';
import { PeriodNavigator, getPeriodBounds } from '../manager/agenda/PeriodNavigator';
import { UpcomingMissionsAgenda } from '../manager/agenda/UpcomingMissionsAgenda';
import type { SiteInfo } from '../../lib/execution';
import type { Service } from '../../lib/services';
import type { TaskConflict } from '../../lib/conflictDetection';
import type { Profile } from '../../hooks/useTeamViewData';
import type { PeriodType } from '../manager/agenda/PeriodNavigator';
import type { EnrichedMission } from '../manager/agenda/types';
import type { AssignedTaskWithDetails } from '../../lib/execution';

interface TimelineTaskFormatted {
  id: string;
  title: string;
  startTime: string;
  duration: number;
  status: string;
  operatorName: string;
  operatorUsername: string;
  operatorId: string;
  isRecurring: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
  serviceColor?: string;
  serviceName?: string;
}

export interface TeamViewMobileLayoutProps {
  sites: SiteInfo[];
  services: Service[];
  selectedSiteId: string | null;
  selectedServiceIds: string[];
  selectedProfileIds: string[];
  selectionMode: 'auto' | 'manual';
  onSiteChange: (siteId: string) => void;
  onServiceChange: (serviceIds: string[]) => void;
  onOperatorSelectionChange: (profileIds: string[]) => void;
  onSelectionModeChange: (mode: 'auto' | 'manual') => void;
  allProfilesForSite: Profile[];
  loading: boolean;
  error: string | null;
  isRefreshing: boolean;
  onManualRefresh: () => void;
  onRetryLoadTasks: () => void;
  timelineTasks: TimelineTaskFormatted[];
  dateObject: Date;
  displayedOperators: Profile[];
  onTaskClick: (task: any) => void;
  onTaskDrop: (taskId: string, fromOperatorId: string, toOperatorId: string, task: any, newScheduledTime: string) => void;
  rollbackRef: React.MutableRefObject<(() => void) | null>;
  scrollTriggerKey: number;
  conflictingTaskIds: Set<string>;
  activeConflicts: TaskConflict[];
  conflictsLoading: boolean;
  onConflictClick: (userId: string, time: string, serviceId1: string | null, serviceId2: string | null) => void;
  selectedDate: string;
  showCalendar: boolean;
  onToggleCalendar: () => void;
  onGoToToday: () => void;
  onDateSelect: (date: string) => void;
  onPrint: () => void;
  isPrinting: boolean;
  viewMode: 'calendar' | 'list';
  onViewModeChange: (mode: 'calendar' | 'list') => void;
  listPeriodType: PeriodType;
  listReferenceDate: Date;
  onListNavigate: (date: Date) => void;
  onListPeriodTypeChange: (type: PeriodType) => void;
  listTasks: EnrichedMission[];
  listLoading: boolean;
  listError: string | null;
  listIsRefreshing: boolean;
  onListRefresh: () => void;
  onListTaskClick: (task: AssignedTaskWithDetails) => void;
}

export function TeamViewMobileLayout({
  sites,
  services,
  selectedSiteId,
  selectedServiceIds,
  selectedProfileIds,
  selectionMode,
  onSiteChange,
  onServiceChange,
  onOperatorSelectionChange,
  onSelectionModeChange,
  allProfilesForSite,
  loading,
  error,
  isRefreshing,
  onManualRefresh,
  onRetryLoadTasks,
  timelineTasks,
  dateObject,
  displayedOperators,
  onTaskClick,
  onTaskDrop,
  rollbackRef,
  scrollTriggerKey,
  conflictingTaskIds,
  activeConflicts,
  conflictsLoading,
  onConflictClick,
  selectedDate,
  showCalendar,
  onToggleCalendar,
  onGoToToday,
  onDateSelect,
  onPrint,
  isPrinting,
  viewMode,
  onViewModeChange,
  listPeriodType,
  listReferenceDate,
  onListNavigate,
  onListPeriodTypeChange,
  listTasks,
  listLoading,
  listError,
  listIsRefreshing,
  onListRefresh,
  onListTaskClick,
}: TeamViewMobileLayoutProps) {
  const agendaBounds = getPeriodBounds(listReferenceDate, listPeriodType);

  return (
    <div className="pb-20 pt-[env(safe-area-inset-top)]">
      <div className="sticky top-0 bg-white border-b border-gray-200 -mx-4 px-4 pb-4" style={{ zIndex: Z_STICKY_HEADER }}>
        <div className="flex items-center justify-between gap-3 mb-2">
          {viewMode === 'calendar' ? (
            <DateSelectorButton
              currentDate={selectedDate}
              showCalendar={showCalendar}
              onToggleCalendar={onToggleCalendar}
              onGoToToday={onGoToToday}
            />
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex items-center gap-2 flex-shrink-0">
            <ViewSwitch value={viewMode} onChange={onViewModeChange} />
            <button
              onClick={onPrint}
              disabled={isPrinting || (viewMode === 'calendar' ? selectedProfileIds.length === 0 : listTasks.length === 0)}
              className="flex items-center justify-center w-9 h-9 bg-white border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              title={viewMode === 'list' ? 'Télécharger la liste en PDF' : 'Télécharger le planning en PDF'}
              aria-label={viewMode === 'list' ? 'Télécharger la liste en PDF' : 'Télécharger le planning en PDF'}
            >
              <Printer size={18} className={isPrinting ? 'animate-pulse' : ''} />
            </button>
            <button
              onClick={viewMode === 'list' ? onListRefresh : onManualRefresh}
              disabled={viewMode === 'list' ? listIsRefreshing : (isRefreshing || selectedProfileIds.length === 0)}
              className="flex items-center justify-center w-9 h-9 bg-white border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              title="Rafraîchir les données"
              aria-label="Rafraîchir les données"
            >
              <RefreshCw size={18} className={(viewMode === 'list' ? listIsRefreshing : isRefreshing) ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {sites.length > 1 && (
            <SiteSelector
              sites={sites}
              selectedSiteId={selectedSiteId}
              onSiteChange={onSiteChange}
              compact
            />
          )}
          {services.length > 0 && (
            <ServiceSelector
              services={services}
              selectedServiceIds={selectedServiceIds}
              onServiceChange={onServiceChange}
              compact
            />
          )}
          <MultiProfileSelector
            profiles={allProfilesForSite}
            selectedProfileIds={selectedProfileIds}
            onSelectionChange={onOperatorSelectionChange}
            selectionMode={selectionMode}
            onSelectionModeChange={onSelectionModeChange}
            compact
          />
        </div>

        {viewMode === 'calendar' && (
          <InlineMonthCalendar
            selectedDate={selectedDate}
            onSelectDate={onDateSelect}
            isOpen={showCalendar}
          />
        )}
      </div>

      <div>
        {viewMode === 'calendar' ? (
          <>
            {loading ? (
              <TimelineLoadingState
                operatorCount={selectedProfileIds.length > 1 ? selectedProfileIds.length : 1}
              />
            ) : error ? (
              <div className="bg-white border border-red-200 rounded-lg p-12">
                <div className="text-center">
                  <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={onRetryLoadTasks}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : selectedServiceIds.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="text-center">
                  <Layers className="mx-auto text-primary-400 mb-4" size={48} />
                  <p className="text-gray-600">
                    Sélectionnez un ou plusieurs services pour afficher les plannings de votre équipe
                  </p>
                </div>
              </div>
            ) : selectedProfileIds.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="text-center">
                  <Users className="mx-auto text-primary-400 mb-4" size={48} />
                  <p className="text-gray-600">
                    Veuillez sélectionner au moins un membre de l'équipe pour afficher le planning
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {activeConflicts.length > 0 && (
                  <ConflictSummaryPanel
                    conflicts={activeConflicts}
                    loading={conflictsLoading}
                    onConflictClick={onConflictClick}
                    defaultCollapsed
                  />
                )}
                <DayTimeline
                  tasks={timelineTasks}
                  date={dateObject}
                  operatorIds={selectedProfileIds}
                  operators={displayedOperators}
                  onTaskClick={onTaskClick}
                  onTaskDrop={onTaskDrop}
                  rollbackRef={rollbackRef}
                  scrollTriggerKey={scrollTriggerKey}
                  conflictingTaskIds={conflictingTaskIds}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <PeriodNavigator
              periodType={listPeriodType}
              referenceDate={listReferenceDate}
              onNavigate={onListNavigate}
              onPeriodTypeChange={onListPeriodTypeChange}
            />
            <UpcomingMissionsAgenda
              tasks={listTasks}
              periodStart={agendaBounds.start}
              periodEnd={agendaBounds.end}
              services={services}
              loading={listLoading}
              error={listError}
              onMissionClick={onListTaskClick}
              periodType={listPeriodType}
              isDesktop={false}
              onRetry={onListRefresh}
              hasServiceFilter={selectedServiceIds.length > 0}
              hasProfileFilter={selectedProfileIds.length > 0}
            />
          </>
        )}
      </div>
    </div>
  );
}
